import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, ChevronLeft, ChevronRight, Users2 } from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 10;

function formatDT(iso) {
  if (!iso) return { d: "—", t: "" };
  try {
    const dt = new Date(iso);
    return {
      d: dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
      t: dt.toLocaleTimeString("en-IN", {
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      }),
    };
  } catch { return { d: "—", t: "" }; }
}

function rankPill(rank) {
  const map = {
    Member: "text-slate-700",
    Executive: "text-blue-700",
    Promoter: "text-emerald-700",
    Motivator: "text-purple-700",
    "Sr. Motivator": "text-fuchsia-700",
    "Co-ordinator": "text-amber-800",
    "Sr. Co-ordinator": "text-orange-800",
    Manager: "text-red-700",
    Director: "text-rose-800",
  };
  return `font-bold ${map[rank] || "text-slate-700"}`;
}

export default function LevelTeam() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [q, setQ] = useState("");
  const [fRank, setFRank] = useState("All");
  const [fLevel, setFLevel] = useState("All");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/level");
      setItems(data.items || []);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => { setPage(1); }, [q, fRank, fLevel, fFrom, fTo]);

  const uniqueRanks = useMemo(() => {
    const s = new Set(items.map((h) => h.rank));
    return ["All", ...Array.from(s)];
  }, [items]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const fromT = fFrom ? new Date(fFrom).getTime() : null;
    const toT = fTo ? new Date(fTo).getTime() + 24 * 3600 * 1000 - 1 : null;
    return items.filter((h) => {
      if (qq) {
        const hay = `${h.user_code} ${h.name || ""} ${h.sponsor_user_code || ""} ${h.sponsor_name || ""}`
          .toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      if (fRank !== "All" && h.rank !== fRank) return false;
      if (fLevel !== "All" && String(h.level) !== fLevel) return false;
      if (fromT || toT) {
        const t = h.activation_date ? new Date(h.activation_date).getTime() : 0;
        if (fromT && t < fromT) return false;
        if (toT && t > toT) return false;
      }
      return true;
    });
  }, [items, q, fRank, fLevel, fFrom, fTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const updatedLabel = updatedAt
    ? updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })
    : "—";

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">

        {/* Purple heading card */}
        <section
          className="rounded-2xl shadow-xl overflow-hidden text-white"
          style={{
            background: "linear-gradient(135deg, #ede9fe 0%, #ede9fe 50%, #ddd6fe 100%)",
          }}
        >
          <div className="p-6 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-purple-700"
                  style={{ fontFamily: "Outfit, sans-serif" }}>
                Level Team
              </h1>
              <p className="text-slate-700 mt-1 text-sm">
                View your downlines by level (1-30)
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-700">Updated: <b>{updatedLabel}</b></span>
              <button
                data-testid="level-team-refresh"
                onClick={load}
                className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg px-4 py-2 shadow-md active:scale-[0.98] transition"
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-t-none rounded-b-2xl p-5 shadow-inner">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Search</label>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="lt-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search User..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Filter by Rank</label>
                <select
                  data-testid="lt-filter-rank"
                  value={fRank}
                  onChange={(e) => setFRank(e.target.value)}
                  className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  {uniqueRanks.map((r) => (
                    <option key={r} value={r}>{r === "All" ? "All Rank" : r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Filter by Level (1-30)</label>
                <select
                  data-testid="lt-filter-level"
                  value={fLevel}
                  onChange={(e) => setFLevel(e.target.value)}
                  className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                >
                  <option value="All">All Level</option>
                  {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={String(n)}>Level {n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">From Date</label>
                <input
                  data-testid="lt-filter-from"
                  type="date"
                  value={fFrom}
                  onChange={(e) => setFFrom(e.target.value)}
                  className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">To Date</label>
                <input
                  data-testid="lt-filter-to"
                  type="date"
                  value={fTo}
                  onChange={(e) => setFTo(e.target.value)}
                  className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div
                data-testid="lt-total-downlines"
                className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-xl px-4 py-2"
              >
                <Users2 size={16} className="text-purple-600" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    Total Downlines
                  </div>
                  <div className="text-2xl font-black text-red-700 leading-none mt-0.5"
                       style={{ fontFamily: "Outfit, sans-serif" }}>
                    {items.length}
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Filtered: <b className="text-slate-800">{filtered.length}</b>
              </div>
            </div>
          </div>
        </section>

        {/* Table */}
        <section data-testid="level-team-table-section"
                 className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className="text-white text-[11px] uppercase tracking-widest font-bold"
                style={{
                  background:
                    "linear-gradient(90deg, #7c3aed 0%, #9333ea 50%, #a855f7 100%)",
                }}
              >
                <tr>
                  <th className="text-left px-3 py-3">S.No.</th>
                  <th className="text-left px-3 py-3">User ID</th>
                  <th className="text-left px-3 py-3">User Name</th>
                  <th className="text-left px-3 py-3">Sponsor ID</th>
                  <th className="text-left px-3 py-3">Sponsor Name</th>
                  <th className="text-left px-3 py-3">Level</th>
                  <th className="text-left px-3 py-3">Package</th>
                  <th className="text-left px-3 py-3">Activation Date</th>
                  <th className="text-left px-3 py-3">Rank</th>
                  <th className="text-left px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody data-testid="lt-table" className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-10 text-slate-500">Loading…</td></tr>
                ) : pageItems.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-10 text-slate-500">
                    No matching members.
                  </td></tr>
                ) : pageItems.map((h, i) => {
                  const a = formatDT(h.activation_date);
                  return (
                    <tr key={h.user_code} className="hover:bg-purple-50/40">
                      <td className="px-3 py-3 text-red-600 font-bold">{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="px-3 py-3 font-mono text-red-600 font-semibold">{h.user_code}</td>
                      <td className="px-3 py-3 font-semibold">{h.name}</td>
                      <td className="px-3 py-3 font-mono text-red-600 font-semibold">{h.sponsor_user_code || "—"}</td>
                      <td className="px-3 py-3">{h.sponsor_name || "—"}</td>
                      <td className="px-3 py-3">
                        <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                          Level {h.level}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-slate-700">{h.package}</td>
                      <td className="px-3 py-3 text-slate-700 whitespace-nowrap">
                        <div className="font-semibold">{a.d},</div>
                        <div className="text-xs text-slate-500">{a.t}</div>
                      </td>
                      <td className={`px-3 py-3 ${rankPill(h.rank)}`}>{h.rank}</td>
                      <td className="px-3 py-3">
                        {h.activated ? (
                          <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                            Active
                          </span>
                        ) : (
                          <span className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                            Inactive
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-600">
              Showing <b>{filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</b> of{" "}
              <b>{filtered.length}</b> members
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="lt-page-prev"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40"
              >
                <ChevronLeft size={14} /> Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5)
                .map((n) => (
                  <button
                    key={n}
                    data-testid={`lt-page-${n}`}
                    onClick={() => setPage(n)}
                    className={`h-8 min-w-8 px-2 rounded-lg text-xs font-semibold border ${
                      n === page
                        ? "bg-purple-600 text-white border-purple-600"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              <button
                data-testid="lt-page-next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
